import DialogForm from "./DialogForm";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import React, { useEffect } from "react";
import { DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import useAppStore from "@/store/appStore";
import { yupResolver } from "@hookform/resolvers/yup";
import { Save, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Textarea } from "./ui/textarea";
import { Category, CategoryFormData } from "@/model/entities";
import { toast } from "sonner";
import { useCurrentRestaurant } from "@/hooks/useCurrentRestaurant";
import { useCategories } from "@/query/useCategories";
import { usePlan } from "@/hooks/usePlan";
// ...existing code...
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";

interface CategoryDialogFormProps {
  open: boolean;
  onClose: () => void;
  category?: Category;
  isAllowedToAddCategory: boolean;
  // optional restaurantId to allow parent to control which restaurant the dialog operates on
  restaurantId?: string;
}

const CategoryDialogForm: React.FC<CategoryDialogFormProps> = ({
  open,
  onClose,
  category,
  isAllowedToAddCategory = false,
  restaurantId,
}) => {
  const { t } = useTranslation();
  const { darkMode } = useAppStore();

  // If parent provides a restaurantId prop, use that. Otherwise fall back to current restaurant from hook.
  const { restaurantId: currentRestaurantId } = useCurrentRestaurant();
  const effectiveRestaurantId = restaurantId ?? currentRestaurantId;
  const { createCategory, updateCategory } = useCategories(
    effectiveRestaurantId
  );
  const { isAllowedToAddCategory: canAddCategoryFromHook, plan } = usePlan();

  // For editing, always allow (don't check limits).
  // For adding: use prop OR hook value (whichever allows it)
  // This ensures both admin pages (which pass true) and manager pages (which compute via hook) work correctly
  const canAddCategory = category
    ? true
    : isAllowedToAddCategory || canAddCategoryFromHook;

  console.log("CategoryDialogForm render:", {
    plan,
    canAddCategory,
    isAllowedToAddCategory,
    canAddCategoryFromHook,
  });

  const { name, description } = category ?? {};

  const validationSchema = yup.object<CategoryFormData>().shape({
    name: yup
      .string()
      .required(t("input.required"))
      .max(20, t("validation.maxLength", { max: 20 })),
    description: yup.string().notRequired().default(""),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CategoryFormData>({
    defaultValues: { name, description } as CategoryFormData,
    resolver: yupResolver(validationSchema),
  });

  // When the dialog opens or the category changes, reset the form to the
  // category values (or empty for add). This ensures the edit form is
  // populated with the selected category's data.
  useEffect(() => {
    // If category is provided, set its values; otherwise clear the form.
    reset({
      name: category?.name ?? "",
      description: category?.description ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, open]);

  // Calculate if submit button should be disabled
  // Allow clicking submit even if form not dirty/valid; handleSubmit will validate.
  const isSubmitDisabled = updateCategory.isPending || createCategory.isPending;

  const handleSubmitForm = async (data: CategoryFormData) => {
    try {
      if (category) {
        await updateCategory.mutateAsync({
          categoryId: category.id!,
          category: data,
        });
      } else {
        await createCategory.mutateAsync(data);
      }
      handleClose();
    } catch (error) {
      console.error(error);
      toast.error(t("categoryForm.error"));
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <DialogForm
      open={open}
      title={
        category
          ? t("categoryForm.editCategory")
          : t("categoryForm.addCategory")
      }
      onClose={handleClose}
      description={t("categoryForm.dialogDescription")}
      footer={
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className={darkMode ? "hover:bg-gray-700 border-gray-600" : ""}
            onClick={handleClose}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            className="bg-orange-500 hover:bg-orange-600"
            onClick={handleSubmit(handleSubmitForm)}
            disabled={
              isSubmitDisabled ||
              // Disable if we're adding (not editing) and not allowed to add
              (!category && !canAddCategory)
            }
          >
            <Save className="w-4 h-4 mr-2" />
            {t("save")}
          </Button>
        </DialogFooter>
      }
    >
      <form className="space-y-4">
        <div className="grid w-full max-w-sm items-center gap-1.5">
          <Label htmlFor="name">{t("categoryForm.categoryName")}</Label>
          <Input id="name" {...register("name", { required: true })} />
          {errors.name && (
            <p className="text-red-500 text-sm">{errors.name.message}</p>
          )}
        </div>
        <div className="grid w-full max-w-sm items-center gap-1.5">
          <Label htmlFor="description">{t("categoryForm.categoryDesc")}</Label>
          <Textarea
            id="description"
            {...register("description", { required: true })}
          />
          {errors.description && (
            <p className="text-red-500 text-sm">{errors.description.message}</p>
          )}
        </div>
        {!canAddCategory && (
          <Alert variant="destructive" className="mt-2">
            <AlertCircle className="h-4 w-4 mr-2 text-red-600" aria-hidden />
            <AlertTitle>{t("categoryForm.upgradeTitle")}</AlertTitle>
            <AlertDescription>
              {t("categoryForm.upgradeDescription")}
            </AlertDescription>
          </Alert>
        )}
      </form>
    </DialogForm>
  );
};

export default CategoryDialogForm;
